import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fmtNum } from '../../lib/formatHelpers';

/**
 * V2 CreatorsListTable — Liste paginée + sortable des créateurs.
 *
 *  - Desktop : table avec colonnes Creator / Server / Tier / Last live / Valid days / Status
 *  - Mobile  : cards avec mêmes infos (R3)
 *
 * Props :
 *  - data : array<{ id, display_name, tiktok_username, server, tier_current,
 *                   tier, status, last_live_date, valid_days,
 *                   days_since_joining, manager_name }>
 *  - loading
 *  - basePath : '/admin/creators' or '/manager/team' — used for Link targets
 *  - pageSize : default 50
 */
const SortHeader = ({ field, label, sort, onSort, align = 'left' }) => {
  const active = sort.field === field;
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className={`flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold transition-colors ${
        active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
      } ${align === 'right' ? 'ml-auto' : ''}`}
    >
      {label}
      {active && (
        <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
          <path d={sort.dir === 'asc' ? 'M4 2l3 4H1z' : 'M4 6L1 2h6z'} fill="currentColor" />
        </svg>
      )}
    </button>
  );
};

const StatusPill = ({ status }) => {
  const { t } = useTranslation();
  const cls =
    status === 'active'    ? 'bg-success/10 text-success'
  : status === 'inactive'  ? 'bg-muted text-muted-foreground'
  : status === 'suspended' ? 'bg-warning/10 text-warning'
  : 'bg-muted text-muted-foreground';
  return (
    <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full ${cls}`}>
      {t(`creatorsV2.status.${status || 'unknown'}`, status || '—')}
    </span>
  );
};

const CreatorsListTable = ({ data = [], loading = false, basePath = '/admin/creators', pageSize = 50 }) => {
  const { t } = useTranslation();
  const [sort, setSort] = useState({ field: 'display_name', dir: 'asc' });
  const [page, setPage] = useState(0);

  const sorted = useMemo(() => {
    const arr = [...data];
    arr.sort((a, b) => {
      const av = a[sort.field];
      const bv = b[sort.field];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') return sort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sort.dir === 'asc' ? av - bv : bv - av;
    });
    return arr;
  }, [data, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pageData = sorted.slice(page * pageSize, (page + 1) * pageSize);

  const handleSort = (field) => {
    setSort((s) => (s.field === field ? { field, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' }));
    setPage(0);
  };

  if (loading) return <div className="rounded-2xl bg-card border border-border h-64 skeleton" />;

  return (
    <section className="rounded-2xl bg-card border border-border overflow-hidden">
      <div className="px-5 py-3 border-b border-border bg-surface-2 flex items-center justify-between">
        <div className="font-heading font-semibold text-foreground text-sm">
          {t('creatorsV2.list.title', 'Creators')}
          <span className="ml-2 text-xs text-muted-foreground font-normal">({fmtNum(sorted.length)})</span>
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-surface-2">
              <th className="text-left px-4 py-2.5">
                <SortHeader field="display_name" label={t('creatorsV2.list.colCreator', 'Creator')} sort={sort} onSort={handleSort} />
              </th>
              <th className="text-left px-3 py-2.5">
                <SortHeader field="server" label={t('creatorsV2.list.colServer', 'Server')} sort={sort} onSort={handleSort} />
              </th>
              <th className="text-left px-3 py-2.5">
                <SortHeader field="tier_current" label={t('creatorsV2.list.colTier', 'Tier')} sort={sort} onSort={handleSort} />
              </th>
              <th className="text-left px-3 py-2.5">
                <SortHeader field="manager_name" label={t('creatorsV2.list.colManager', 'Manager')} sort={sort} onSort={handleSort} />
              </th>
              <th className="text-right px-3 py-2.5">
                <SortHeader field="last_live_date" label={t('creatorsV2.list.colLastLive', 'Last live')} sort={sort} onSort={handleSort} align="right" />
              </th>
              <th className="text-right px-3 py-2.5">
                <SortHeader field="valid_days" label={t('creatorsV2.list.colValidDays', 'Valid days')} sort={sort} onSort={handleSort} align="right" />
              </th>
              <th className="text-right px-4 py-2.5">
                <SortHeader field="status" label={t('creatorsV2.list.colStatus', 'Status')} sort={sort} onSort={handleSort} align="right" />
              </th>
            </tr>
          </thead>
          <tbody>
            {pageData.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-sm text-muted-foreground">
                  {t('creatorsV2.list.empty', 'No creators match these filters.')}
                </td>
              </tr>
            )}
            {pageData.map((c) => (
              <tr key={c.id} className="border-b border-border last:border-b-0 hover:bg-muted/40">
                <td className="px-4 py-2.5">
                  <Link to={`${basePath}/${c.id}`} className="text-foreground hover:text-primary transition-colors block">
                    <div className="font-medium truncate max-w-[220px]">{c.display_name || c.tiktok_username || '—'}</div>
                    {c.tiktok_username && (
                      <div className="text-xs text-muted-foreground font-data truncate max-w-[220px]">@{c.tiktok_username}</div>
                    )}
                  </Link>
                </td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground">{c.server || '—'}</td>
                <td className="px-3 py-2.5 text-xs">
                  <span className="px-2 py-0.5 rounded-full bg-muted text-foreground font-data">
                    T{c.tier_current ?? c.tier ?? '—'}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground truncate max-w-[180px]">{c.manager_name || '—'}</td>
                <td className="px-3 py-2.5 text-right text-xs tabular-nums text-muted-foreground">
                  {c.last_live_date ? new Date(c.last_live_date).toLocaleDateString() : '—'}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">{fmtNum(c.valid_days)}</td>
                <td className="px-4 py-2.5 text-right">
                  <StatusPill status={c.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-border">
        {pageData.length === 0 && (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground">
            {t('creatorsV2.list.empty', 'No creators match these filters.')}
          </div>
        )}
        {pageData.map((c) => (
          <Link key={c.id} to={`${basePath}/${c.id}`} className="block px-4 py-3 hover:bg-muted/40 transition-colors">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-foreground truncate">{c.display_name || c.tiktok_username || '—'}</div>
                {c.tiktok_username && (
                  <div className="text-xs text-muted-foreground font-data truncate">@{c.tiktok_username}</div>
                )}
                <div className="text-xs text-muted-foreground mt-1 truncate">
                  {c.server || '—'} · {c.manager_name || t('creatorsV2.list.noManager', 'No manager')}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <span className="px-2 py-0.5 rounded-full bg-muted text-foreground text-xs font-data">
                  T{c.tier_current ?? c.tier ?? '—'}
                </span>
                <StatusPill status={c.status} />
              </div>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
              <div>
                <div className="text-[9px] uppercase tracking-widest text-muted-foreground/70">{t('creatorsV2.list.colLastLive', 'Last live')}</div>
                <div className="font-medium tabular-nums">
                  {c.last_live_date ? new Date(c.last_live_date).toLocaleDateString() : '—'}
                </div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-widest text-muted-foreground/70">{t('creatorsV2.list.colValidDays', 'Valid days')}</div>
                <div className="font-medium tabular-nums">{fmtNum(c.valid_days)}</div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 py-3 border-t border-border flex items-center justify-between text-xs">
          <span className="text-muted-foreground">
            {t('creatorsV2.list.page', 'Page')} {page + 1} / {totalPages}
          </span>
          <div className="flex gap-1">
            <button type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="px-3 py-1 rounded border border-border hover:bg-muted disabled:opacity-40">
              {t('common.prev', 'Prev')}
            </button>
            <button type="button" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="px-3 py-1 rounded border border-border hover:bg-muted disabled:opacity-40">
              {t('common.next', 'Next')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default CreatorsListTable;
